
import { Component, Input, OnInit, signal, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminStageDef } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from 'primeng/inputtext';
import { PickListModule } from 'primeng/picklist';
import { SelectModule } from 'primeng/select';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { SkillItem, StageAdminService } from '@shared-angular/services/stage-admin.service';
import { FormForBooleanComponent } from "../../../../../../../../shared/components/ai-bot/utils/form-for-boolean/form-for-boolean.component";
import { HintComponent } from "@shared-angular/components/hint/hint.component";

@Component({
  selector: 'app-stage-manager-detail',
  imports: [RouterLink, FormsModule,
    FormForBooleanComponent,
    InputTextModule,
    PickListModule,
    SelectModule,
    ButtonModule,
    HintComponent,
    GotoBlocklyDirective,
    IconFieldModule,
    InputIconModule
],
  templateUrl: './stage-manager-detail.component.html',
  styleUrl: './stage-manager-detail.component.scss'
})
export class StageManagerDetailComponent implements OnInit {
  @Input() public selectedStageId?: string;
  public readonly closeDrawer = output();
  protected separatedSkillList = signal<string[]>([]);
  protected targetSkillList = signal<string[]>([]);
  protected availableSkillList: SkillItem[] = [];
  protected jsEnterDetectedErrors: boolean = false;
  protected jsExitDetectedErrors: boolean = false;

  protected stageHidden = $localize`:@@stage-hidden:Stage verstecken`;
  protected stageFooter = $localize`:@@stage-Footer:Fußzeile anzeigen`;
  protected stageForce = $localize`:@@stage-force:Skillauswahl forcieren`;

  constructor(
    protected stageAdmin: StageAdminService,
    protected rss: RouteShareService,
    public editorItemService: EditorItemsService
  ) { }

  public async ngOnInit(): Promise<void> {
    if (this.selectedStage) {
      this.targetSkillList.set(this.selectedStage.skills);
    }
    this.availableSkillList = await this.stageAdmin.getSkillsForStage();
    const tempArray = this.availableSkillList.map(item => item.name);
    this.separatedSkillList.set(tempArray.filter(item => !this.selectedStage?.skills.includes(item)));
  }


  protected get selectedStage(): AdminStageDef | undefined {
    return this.stageAdmin.stages.find((s) => s._id === this.selectedStageId);
  }

  protected selectPrevRule() {
    const currentIndex = this.stageAdmin.stages.findIndex((s) => s._id === this.selectedStageId);
    let newIndex = currentIndex - 1;
    if (newIndex === -1) { newIndex = this.stageAdmin.stages.length - 1; }
    const newStage = this.stageAdmin.stages[newIndex];
    if (newStage) {
      this.selectedStageId = newStage._id;
      if (this.selectedStage) {
        this.targetSkillList.set(this.selectedStage.skills);
      }
      const tempArray = this.availableSkillList.map(item => item.name);
      this.separatedSkillList.set(tempArray.filter(item => !this.selectedStage?.skills.includes(item)));
    }
  }

  protected selectNextRule() {
    const currentIndex = this.stageAdmin.stages.findIndex((s) => s._id === this.selectedStageId);
    let newIndex = currentIndex + 1;
    if (newIndex >= this.stageAdmin.stages.length) { newIndex = 0; }
    const newStage = this.stageAdmin.stages[newIndex];
    if (newStage) {
      this.selectedStageId = newStage._id;
      if (this.selectedStage) {
        this.targetSkillList.set(this.selectedStage.skills);
      }
      const tempArray = this.availableSkillList.map(item => item.name);
      this.separatedSkillList.set(tempArray.filter(item => !this.selectedStage?.skills.includes(item)));
    }
  }

  protected async saveSkillSelection() {
    if (this.selectedStage) {
      this.selectedStage.skills = this.targetSkillList();
      await this.stageAdmin.updateStagesField(this.selectedStage._id, 'skills', this.selectedStage.skills);
    }
  }

  protected getLinkForSkill(skill: string) {
    const skillObject = this.availableSkillList.find(item => item.name === skill);
    if (!skillObject) return "";
    if (skillObject.type === 'datatrunk') return "/bot-admin/" + this.rss.botConfig._id + "/databases";
    if (skillObject.type === 'mcp') return "/bot-admin/" + this.rss.botConfig._id + "/mcp-server";
    if (skillObject.type === 'filesearch') return "/bot-admin/" + this.rss.botConfig._id + "/documents";
    if (skillObject.type === 'customtoolkit') return "/bot-admin/" + this.rss.botConfig._id + "/if-then";
    if (skillObject.type === 'websitescrape') return "/bot-admin/" + this.rss.botConfig._id + "/offline-websites";
    if (skillObject.type === 'websitesearch' || skillObject.type === 'schnittstelle') return "/bot-admin/" + this.rss.botConfig._id + "/online-sources";
    return "/bot-admin/" + this.rss.botConfig._id + "/webhooks";
  }

  protected getQueryParamsForSkill(skill: string): { open?: string } {
    const skillObject = this.availableSkillList.find(item => item.name === skill);
    if (!skillObject) return {};
    if (skillObject.type === 'mcp') return {};
    return { open: skillObject.id };
  }

  protected getIconForSkill(skill: string) {
    const skillObject = this.availableSkillList.find(item => item.name === skill);
    if (!skillObject) return "";
    if (skillObject.type === 'datatrunk') return 'ri-database-2-fill';
    if (skillObject.type === 'filesearch') return 'ri-article-fill';
    if (skillObject.type === 'customtoolkit') return 'ri-swap-2-fill';
    if (skillObject.type === 'websitescrape') return 'ri-cloud-off-fill';
    if (skillObject.type === 'websitesearch' || skillObject.type === 'schnittstelle') return 'ri-cloud-fill';
    if (skillObject.type === 'mcp') return 'ri-server-fill';
    if (skillObject.type as string === "webhook") return 'ri-webhook-fill';
    return "ri-error-warning-line";
  }
}
